import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import styles from "./NeedHelpTickets.module.css";

// ADDED: new file. Phone browsers (Android Chrome, iPhone) cannot show a PDF
// inside an <iframe> - they only show an "Open" button. This viewer draws the
// PDF pages itself (pdf.js), so the PDF scrolls inside the popup on phones too.
// The laptop keeps using the normal <iframe> (see NeedHelpTicketDetails.jsx).

// pdf.js needs a worker file. It is loaded from the CDN (same version as the
// installed package), so no bundler setup is needed.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// true when this browser cannot show a PDF inside an <iframe>
export const needsPdfViewer = () => {
  if (typeof navigator === "undefined") return false;
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  return navigator.pdfViewerEnabled === false;
};

const NeedHelpPdfViewer = ({ url, title }) => {
  const wrapRef = useRef(null);
  const pagesRef = useRef(null);
  // "loading" | "ready" | "error"
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;
    const holder = pagesRef.current;
    if (holder) holder.innerHTML = "";
    setStatus("loading");

    const run = async () => {
      try {
        pdfDoc = await pdfjsLib.getDocument({ url }).promise;
        const width = wrapRef.current?.clientWidth || 300;
        const ratio = Math.min(window.devicePixelRatio || 1, 2);

        for (let n = 1; n <= pdfDoc.numPages; n += 1) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const scale = width / base.width;
          const viewport = page.getViewport({ scale: scale * ratio });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = "100%";
          canvas.style.display = "block";
          canvas.className = styles.pdfPage;
          if (cancelled) return;
          holder.appendChild(canvas);

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
          if (n === 1 && !cancelled) setStatus("ready"); // show page 1 at once
        }
      } catch (_e) {
        if (!cancelled) setStatus("error");
      }
    };
    run();

    return () => {
      cancelled = true;
      if (pdfDoc) pdfDoc.destroy();
    };
  }, [url]);

  return (
    <div ref={wrapRef} className={styles.pdfViewer} aria-label={title}>
      {status === "loading" && (
        <div className={styles.pdfViewerNote}>
          <span className={styles.spinner} aria-label="Loading PDF" />
        </div>
      )}
      {status === "error" && (
        <p className={styles.pdfViewerNote}>
          This PDF can't be shown here. Use "Open in new tab" below.
        </p>
      )}
      <div ref={pagesRef} className={styles.pdfPages} />
    </div>
  );
};

export default NeedHelpPdfViewer;
